import React, { useEffect, useState } from 'react';
import { accountsAPI } from '../../services/api';
import { formatApiError } from '../../utils/errorMessage';

// Accounts saved without a business do not appear under any business or
// under All Businesses. This shows them to their owner with a banner so they
// can be assigned to a business, edited or closed.
export const unassignedSummary = (list) => {
  const rows = Array.isArray(list) ? list : [];
  return { count: rows.length, label: rows.length === 1 ? '1 account is' : `${rows.length} accounts are` };
};

const UnassignedAccounts = ({ businesses = [], refreshKey, onEdit, onChanged, format = (v) => v }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState({});
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await accountsAPI.getAll({ unassigned: 'true' });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setRows([]);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const done = () => { load(); if (onChanged) onChanged(); };

  const assign = async (account) => {
    const businessId = target[account.id];
    if (!businessId) { setError('Choose a business first.'); return; }
    try {
      setError('');
      await accountsAPI.update(account.id, { business_id: businessId });
      done();
    } catch (err) {
      setError(formatApiError(err, 'Could not assign the account'));
    }
  };

  const close = async (account) => {
    if (!window.confirm(`Close ${account.account_name}? It will be marked inactive.`)) return;
    try {
      setError('');
      await accountsAPI.delete(account.id);
      done();
    } catch (err) {
      setError(formatApiError(err, 'Could not close the account'));
    }
  };

  const { count, label } = unassignedSummary(rows);
  if (count === 0) return null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="unassigned-accounts">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-amber-900">
          {label} not assigned to a business, so {count === 1 ? 'it does' : 'they do'} not show in any business list.
        </p>
        <button type="button" className="text-sm font-medium text-amber-900 underline" onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Review'}
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
      {open && (
        <ul className="mt-3 divide-y divide-amber-200">
          {rows.map((account) => (
            <li key={account.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <span className="flex-1 min-w-[12rem]">
                {account.account_name}{account.account_number_last4 ? ` ${account.account_number_last4}` : ''} ({format(account.current_balance)})
              </span>
              <select
                aria-label={`Business for ${account.account_name}`}
                className="border rounded px-2 py-1"
                value={target[account.id] || ''}
                onChange={(e) => setTarget({ ...target, [account.id]: e.target.value })}
              >
                <option value="">Choose business...</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <button type="button" className="px-3 py-1 rounded bg-blue-600 text-white" onClick={() => assign(account)}>Assign</button>
              {onEdit && <button type="button" className="px-3 py-1 rounded border" onClick={() => onEdit(account)}>Edit</button>}
              <button type="button" className="px-3 py-1 rounded border text-red-700" onClick={() => close(account)}>Close</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default UnassignedAccounts;
